<script>
/* global axios */
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Spinner from 'shared/components/Spinner.vue';
import {
  attachOnboardingKnowledge,
  resolveOnboardingDatasetId,
} from 'dashboard/helper/onboardingKbAttach';

export default {
  name: 'OnboardingWebWidgetConnectModal',
  components: { Spinner },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inboxName: '',
      channelWebsiteUrl: '',
      createdInbox: null,
      // Chatbot creation is treated as part of inbox creation, not a
      // separate optional step — the user can't reach the success/Done
      // state until both the inbox AND its chatbot exist, matching
      // ChoosePlatform.vue's normal (non-onboarding) inbox-creation flow.
      // 'idle' | 'creating' | 'failed' | 'success'
      status: 'idle',
      // Linking the onboarding knowledge base to the new inbox is a
      // separate call after the chatbot exists; it can fail on its own.
      knowledgeAttachFailed: false,
      isAttachingKnowledge: false,
    };
  },
  computed: {
    ...mapGetters({ accountId: 'getCurrentAccountId' }),
    canSubmit() {
      return (
        this.inboxName.trim().length > 0 &&
        this.channelWebsiteUrl.trim().length > 0 &&
        this.status !== 'creating'
      );
    },
    isCreating() {
      return this.status === 'creating';
    },
    hasFailed() {
      return this.status === 'failed';
    },
    isSuccess() {
      return this.status === 'success';
    },
  },
  methods: {
    handleClose() {
      this.$emit('close');
    },
    async handleCreate() {
      if (!this.canSubmit) return;

      this.status = 'creating';
      try {
        const website = await this.$store.dispatch(
          'inboxes/createWebsiteChannel',
          {
            name: this.inboxName,
            greeting_enabled: false,
            greeting_message: '',
            channel: {
              type: 'web_widget',
              website_url: this.channelWebsiteUrl,
              widget_color: '#009CE0',
              welcome_title: '',
              welcome_tagline: '',
            },
          }
        );

        this.createdInbox = website;
        await this.createChatbotForInbox(website.id);
      } catch (error) {
        this.status = 'idle';
        useAlert(
          error?.message ||
            this.$t('ONBOARDING_SHELL.STEPS.CHANNEL.CREATE_ERROR')
        );
      }
    },
    // Same endpoint and no-body contract as ChoosePlatform.vue's
    // createChatbot — defaults to the DahReply/Dify platform bot, which the
    // user can reconfigure later from inbox settings. Sets 'status'
    // directly (not a separate flag) so a failure here keeps the whole
    // modal in a non-success state rather than just this one step.
    async createChatbotForInbox(inboxId) {
      try {
        await axios.post(
          `/api/v1/accounts/${this.accountId}/proxy/knowledge/chatbot/accounts/${this.accountId}/inboxes/${inboxId}/app`
        );
      } catch (error) {
        this.status = 'failed';
        return;
      }
      // The chatbot exists; link the knowledge base built in the previous
      // step. A failure here does not undo the inbox, it shows a retry.
      await this.attachKnowledge();
      this.status = 'success';
      this.$emit('connected', this.createdInbox);
    },
    async attachKnowledge() {
      this.isAttachingKnowledge = true;
      this.knowledgeAttachFailed = !(await attachOnboardingKnowledge({
        accountId: this.accountId,
        inboxId: this.createdInbox.id,
        datasetId: resolveOnboardingDatasetId(this.accountId, this.$store),
      }));
      this.isAttachingKnowledge = false;
    },
    retryCreateChatbot() {
      if (!this.createdInbox) return;
      this.status = 'creating';
      this.createChatbotForInbox(this.createdInbox.id);
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="handleClose">
    <woot-modal-header
      :header-title="$t('ONBOARDING_SHELL.STEPS.CHANNEL.WEB_WIDGET.TITLE')"
      :header-content="
        $t('ONBOARDING_SHELL.STEPS.CHANNEL.WEB_WIDGET.DESCRIPTION')
      "
    />

    <div v-if="!createdInbox" class="flex flex-col gap-4 px-8 pb-8 pt-4">
      <div>
        <label class="text-sm font-medium text-slate-700 dark:text-slate-200">
          {{ $t('ONBOARDING_SHELL.STEPS.CHANNEL.WEB_WIDGET.NAME_LABEL') }}
        </label>
        <input
          v-model="inboxName"
          type="text"
          class="mt-1 block w-full rounded-md border border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
        />
      </div>
      <div>
        <label class="text-sm font-medium text-slate-700 dark:text-slate-200">
          {{ $t('ONBOARDING_SHELL.STEPS.CHANNEL.WEB_WIDGET.URL_LABEL') }}
        </label>
        <input
          v-model="channelWebsiteUrl"
          type="text"
          :placeholder="
            $t('ONBOARDING_SHELL.STEPS.CHANNEL.WEB_WIDGET.URL_PLACEHOLDER')
          "
          class="mt-1 block w-full rounded-md border border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
        />
      </div>
      <woot-button
        class="w-full justify-center"
        :disabled="!canSubmit"
        :is-loading="isCreating"
        @click="handleCreate"
      >
        {{ $t('ONBOARDING_SHELL.STEPS.CHANNEL.WEB_WIDGET.CREATE_BUTTON') }}
      </woot-button>
    </div>

    <!-- Inbox exists but the chatbot isn't confirmed yet — still "creating"
         from the user's point of view, not a separate step. -->
    <div
      v-else-if="isCreating"
      class="flex flex-col items-center gap-3 px-8 pb-8 pt-4 text-center"
    >
      <Spinner size="small" />
      <p class="text-sm text-slate-600 dark:text-slate-400">
        {{ $t('INBOX_MGMT.CHOOSE_PLATFORM.CREATING_CHATBOT') }}
      </p>
    </div>

    <div
      v-else-if="hasFailed"
      class="flex flex-col items-center gap-3 px-8 pb-8 pt-4 text-center"
    >
      <fluent-icon icon="warning" size="32" class="text-red-500" />
      <p class="text-sm text-red-600 dark:text-red-400">
        {{ $t('INBOX_MGMT.CHOOSE_PLATFORM.CREATION_FAILED_DESC') }}
      </p>
      <woot-button class="w-full justify-center" @click="retryCreateChatbot">
        {{ $t('INBOX_MGMT.CHOOSE_PLATFORM.RETRY') }}
      </woot-button>
    </div>

    <div
      v-else-if="isSuccess"
      class="flex flex-col items-center gap-3 px-8 pb-8 pt-4 text-center"
    >
      <div
        class="w-12 h-12 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center animate-bounce"
      >
        <fluent-icon
          icon="checkmark"
          size="24"
          class="text-green-600 dark:text-green-300"
        />
      </div>
      <p class="text-sm text-slate-700 dark:text-slate-200">
        {{
          $t('ONBOARDING_SHELL.STEPS.CHANNEL.WEB_WIDGET.SUCCESS', {
            name: createdInbox.name,
          })
        }}
      </p>
      <div
        v-if="knowledgeAttachFailed"
        class="flex flex-col items-center gap-2"
        data-testid="kb_attach_failed"
      >
        <p class="text-xs text-amber-700 dark:text-amber-400">
          {{ $t('ONBOARDING_SHELL.STEPS.CHANNEL.WEB_WIDGET.KB_ATTACH_FAILED') }}
        </p>
        <woot-button
          variant="clear"
          size="small"
          :is-loading="isAttachingKnowledge"
          data-testid="kb_attach_retry"
          @click="attachKnowledge"
        >
          {{ $t('ONBOARDING_SHELL.STEPS.CHANNEL.WEB_WIDGET.KB_ATTACH_RETRY') }}
        </woot-button>
      </div>
      <woot-button class="w-full justify-center" @click="handleClose">
        {{ $t('ONBOARDING_SHELL.STEPS.CHANNEL.WEB_WIDGET.DONE') }}
      </woot-button>
    </div>
  </woot-modal>
</template>
